import type { ReactNode } from "react";

/**
 * Shared marketing content for the public login + /features screens. One entry
 * per capability: a short tagline (login list) and a fuller description +
 * bullets + real screenshot (features page). No "use client" — this is plain
 * data usable from both server and client components.
 */
export type Capability = {
  slug: string;
  title: string;
  tagline: string;
  description: string;
  bullets: string[];
  shot: string; // screenshot under /public/login
  icon: ReactNode; // inner SVG (rendered inside CapabilityIcon)
};

export const CAPABILITIES: Capability[] = [
  {
    slug: "timing",
    title: "Top-Down Market Timing",
    tagline: "Know the market's regime before you trade a single name.",
    description:
      "Start every session with a clear read on the whole market. The Daily Brief distills breadth, sector performance and index momentum into one glance — so you know whether the tape is with you or against you before you drill into individual stocks.",
    bullets: [
      "Daily Brief: breadth dashboard, sector A/D and index trend in one view",
      "Multi-timeframe trend and a plain-English market-regime read",
      "Purpose-built timing workspaces (Timing Brief, Guppy, Ratio, Buckets)",
    ],
    shot: "/login/daily-brief.jpg",
    icon: <polyline points="3 16 9 10 13 14 21 6" />,
  },
  {
    slug: "breadth",
    title: "Market Breadth & A/D",
    tagline: "See the participation under the surface, not just the index.",
    description:
      "Indices lie; breadth doesn't. Track advance/decline, new highs vs new lows, and the percentage of stocks above their moving averages across NYSE and NASDAQ — with the classic breadth signals (Hindenburg, Eureka, Phoenix and more) flagged as they fire.",
    bullets: [
      "A/D, new highs-lows and % above 20/50/200-day MA, NYSE + NASDAQ",
      "Net new-high/low histograms over the recent sessions",
      "Named breadth signals surfaced the moment they trigger",
    ],
    shot: "/login/dashboard.jpg",
    icon: <path d="M4 20V9M9 20V4M14 20v-8M19 20v-5" />,
  },
  {
    slug: "vpa",
    title: "Volume Price Analysis",
    tagline: "Read what smart money is doing, bar by bar.",
    description:
      "A full HGSI Volume Price Analysis engine (Wyckoff → Tom Williams' VSA → Anna Coulling). Every bar is classified against its volume and spread, colored on the chart, and read across daily, weekly and monthly for a confluence verdict — plus a plain-English narrative, anomaly detector, per-signal backtest and a Wyckoff event timeline.",
    bullets: [
      "~27 VSA flags + VSA-colored price bars and a daily/weekly/monthly read",
      "Plain-English 'VPA Read', price/volume anomalies and a signal backtest",
      "Wyckoff events (springs, upthrusts, climaxes) and volume-node S/R",
    ],
    shot: "/login/vpa.jpg",
    icon: <path d="M3 12h3l3 7 4-15 3 8h5" />,
  },
  {
    slug: "scanner",
    title: "Scanner & Sector Rotation",
    tagline: "Find the setups — and the sectors leading them.",
    description:
      "A top-down scanner over sectors, industries and thousands of stocks. See where money is rotating, then filter the universe with hundreds of signals — trend, momentum, Afiat and pivot-study setups, and the full VPA read — saved as one-click presets.",
    bullets: [
      "Sector → industry → stock rotation, ranked and heat-coded",
      "Hundreds of criteria + one-click presets (incl. VPA setups)",
      "Live-streaming top rows with real-time return ladders",
    ],
    shot: "/login/scanner.jpg",
    icon: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.3-4.3" />
      </>
    ),
  },
  {
    slug: "seasonality",
    title: "Seasonality & Timing Windows",
    tagline: "Trade with the calendar, not against it.",
    description:
      "Know a symbol's historical edge by month, by day of week, and over forward windows. Monthly-return heatmaps, a forward-edge curve with hit rates, and a seasonal projection overlaid on the live chart — so you can line entries up with the times of year that have paid.",
    bullets: [
      "Monthly-return heatmap and day-of-week edge",
      "Forward-edge windows (E5…E60) with hit rate and average move",
      "Seasonal projection overlaid on the price chart",
    ],
    shot: "/login/seasonality.jpg",
    icon: (
      <>
        <rect x="3" y="4" width="18" height="17" rx="2" />
        <path d="M3 9h18M8 2v4M16 2v4" />
      </>
    ),
  },
  {
    slug: "fundamentals",
    title: "Fundamental Analysis",
    tagline: "Growth, quality and valuation at a glance.",
    description:
      "The story behind the chart. Full income statement, balance sheet and cash-flow history, growth CAGRs, margin and return trends, and composite Valuation / Quality / Box / Short-Squeeze scores — alongside analyst grades, insider activity and the latest news.",
    bullets: [
      "Statements, growth, margins and returns over the full history",
      "Composite Valuation / Quality / Box / Short-Squeeze scores",
      "Analyst grades, insider rollups and company news",
    ],
    shot: "/login/fundamentals.jpg",
    icon: (
      <>
        <path d="M4 4h11l5 5v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
        <path d="M14 4v5h5M8 13h8M8 17h5" />
      </>
    ),
  },
];

/** Renders a capability's inner SVG inside a consistently-styled badge. */
export function CapabilityIcon({
  children,
  className = "h-9 w-9",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={`flex ${className} shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent ring-1 ring-accent/20`}>
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        {children}
      </svg>
    </span>
  );
}
